import Fuse from 'fuse.js';
import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionButton, Buttons, CancelButton, Drawer, Icon, SingleSelect, Table, Tooltip } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { colors } from '~/styles';

export default function DepositAccountMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { sageConnectionId } = useParams();

  const [{ mappings, options, isReady }, setState] = useState({ mappings: null, options: null, isReady: false });

  useEffect(() => {
    (async () => {
      const { data: mappings } = await api.www
        .workspaces(workspace.id)
        .sage(sageConnectionId)
        .getDepositAccountMappings();
      const { data: options } = await api.www
        .workspaces(workspace.id)
        .sage(sageConnectionId)
        .getDepositAccountOptions();
      if (!isMounted.current) return;
      setState({ mappings, options, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const updateMapping = (currency, patch) => {
    setDirty(true);
    setState((state) => ({
      ...state,
      mappings: state.mappings.map((m) => (m.currency === currency ? { ...m, ...patch } : m)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Deposit Account Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();
          try {
            await api.www.workspaces(workspace.id).sage(sageConnectionId).updateDepositAccountMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Currency</Table.Column>
                <Table.Column>Sage Deposit Account</Table.Column>
              </Table.BoxHeader>
              <Table.Body>
                {mappings.map((mapping) => {
                  const currencyOptions = options.find((o) => o.currency === mapping.currency);

                  return (
                    <Table.BoxRow key={mapping.currency}>
                      <Table.Cell>{mapping.currency}</Table.Cell>
                      <Table.Cell>
                        <DepositAccountSelect
                          bankAccounts={currencyOptions?.bankAccounts ?? []}
                          directAccounts={currencyOptions?.directAccounts ?? []}
                          value={
                            mapping.sageBankAccountId
                              ? `bank:${mapping.sageBankAccountId}`
                              : mapping.sageDirectAccountId
                                ? `direct:${mapping.sageDirectAccountId}`
                                : null
                          }
                          onChange={(event) => {
                            const [type, id] = (event.target.value || '').split(/:(.*)/s);
                            updateMapping(mapping.currency, {
                              sageBankAccountId: type === 'bank' ? id : null,
                              sageDirectAccountId: type === 'direct' ? id : null,
                            });
                          }}
                        />
                      </Table.Cell>
                    </Table.BoxRow>
                  );
                })}
              </Table.Body>
            </Table>
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}

function DepositAccountSelect({ bankAccounts, directAccounts, value, ...props }) {
  const [filterValue, setFilterValue] = useState('');

  const groups = useMemo(
    () =>
      [
        { label: 'Bank Accounts', options: bankAccounts.map((a) => ({ ...a, id: `bank:${a.id}` })) },
        { label: 'Direct Accounts', options: directAccounts.map((a) => ({ ...a, id: `direct:${a.id}` })) },
      ].filter((group) => group.options.length > 0),
    [bankAccounts, directAccounts],
  );

  const filteredGroups = useMemo(() => {
    if (!filterValue) return groups;
    return groups
      .map((group) => {
        const fuse = new Fuse(group.options, { keys: ['label', 'name'], threshold: 0.1 });
        return { ...group, options: fuse.search(filterValue).map((r) => r.item) };
      })
      .filter((group) => group.options.length > 0);
  }, [groups, filterValue]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    const option = _.find(
      groups.flatMap((group) => group.options),
      { id: value },
    );
    if (!option)
      return {
        id: value,
        name: (
          <Tooltip message="Deposit account not found in Sage Intacct.">
            <Icon icon="warning" color={colors.warning} spaceRight />
            Deposit Account ID: {value.split(/:(.*)/s)[1] ?? value}
          </Tooltip>
        ),
      };
    return option;
  }, [groups, value]);

  return (
    <SingleSelect
      showEmptyOption
      showFilter
      value={value}
      valueRenderer={selectedOption?.label ?? selectedOption?.name ?? ''}
      onFilterChange={(event) => setFilterValue(event.target.value)}
      {...props}>
      {filteredGroups.map((group) => (
        <optgroup key={group.label} label={group.label}>
          {group.options.map((option) => (
            // eslint-disable-next-line react/no-unknown-property
            <option key={option.id} value={option.id} tooltip={option.label ?? option.name}>
              {option.label ?? option.name}
            </option>
          ))}
        </optgroup>
      ))}
    </SingleSelect>
  );
}
